import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

import * as api from "../services";
import AppContext from "../appContext";
import useInteraction from "./useInteractions";
import MutationMessage from "../Components/MutationMessage";
import RemoveDialog from "../Components/RemoveDialog";
import Tab from "./Tab";
import AddSurvey from "./AddSurvey";
import { makeItemId } from "../tools";
import NoAccessMessage from "./NoAccessMessage";

function SurveyList({ list, setOpenDialog }) {
  return list
    .slice(0)
    .reverse()
    .map((tab) => (
      <Grid item xs={12} md={4} key={`Tab-${tab.id}`}>
        <Tab
          id={tab.id}
          title={tab.name}
          interactionType={tab.interactionType}
          isLoading={false}
          setOpenDialog={setOpenDialog}
        />
      </Grid>
    ));
}

export default function UserSurveys() {
  const { t } = useTranslation();
  // local state
  const {
    app: {
      client: { id: clientId, writeAccess, readAccess },
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);
  const [openDialog, setOpenDialog] = useState({
    open: false,
    type: "remove",
    id: "-1",
  });

  // server state
  const queryClient = useQueryClient();
  const { interactions, isInteractionLoading } = useInteraction();
  const removeMutation = useMutation(
    () =>
      api.postLocalInteraction({
        clientId,
        interactionId: openDialog.id,
        interactionJSON: "",
        projectId,
        sessionId,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries(["surveys", clientId]),
    } // trigger the old data to be updated
  );
  const exportMutation = useMutation((data) => api.postPublicInteraction(data));
  useQuery(
    ["survey", clientId, openDialog.id, projectId, sessionId],
    () =>
      api.getLocalInteraction({
        clientId,
        interactionId: openDialog.id,
        projectId,
        sessionId
      }),
    {
      enabled: openDialog.open && openDialog.type === "export",
      onSuccess: (data) => {
        const mySurvey = {
          interactionId: -1,
          interactionJSON: JSON.stringify({
            ...data?.interaction,
            itemId: makeItemId(),
          }),
          name: data?.interaction?.fullQuestion,
          description:
            data.interaction?.itemExtra?.description?.description ?? "",
          _public: 0,
          projectId,
          sessionId
        };
        exportMutation.mutate(mySurvey);
        setOpenDialog((prev) => ({ ...prev, open: false }));
      },
    }
  );

  return (
    <React.Fragment>
      <Typography variant="h6">{t("surveys.subtitle")}</Typography>
      <Divider />

      {readAccess ? (
        <Grid
          container
          spacing={2}
          sx={{ paddingTop: 3, paddingBottom: 3 }}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          {writeAccess ? (
            <Grid item xs={12} md={4}>
              <AddSurvey />
            </Grid>
          ) : null}
          {isInteractionLoading ? null : interactions.length > 0 ? (
            <SurveyList list={interactions} setOpenDialog={setOpenDialog} />
          ) : null}
        </Grid>
      ) : (
        <NoAccessMessage />
      )}

      <MutationMessage
        mutation={removeMutation}
        text={{
          loading: t("common.notifications.removeLoading"),
          success: t("common.notifications.removeSuccess"),
        }}
      />
      <MutationMessage
        mutation={exportMutation}
        text={{
          loading: t("common.notifications.exportLoading"),
          success: t("common.notifications.exportSuccess"),
        }}
      />

      {openDialog.type === "remove" && openDialog.open ? (
        <RemoveDialog
          open={openDialog.open}
          setOpen={setOpenDialog}
          removeFunction={() => removeMutation.mutate()}
          dialogTitle={t("surveys.deleteDialog.title")}
          dialogContentText={t("surveys.deleteDialog.text")}
          dialogActionCancel={t("surveys.deleteDialog.cancel")}
          dialogActionConfirm={t("surveys.deleteDialog.confirm")}
        />
      ) : null}
    </React.Fragment>
  );
}

/* <LoadSurvey xs={getWidth()} /> */
